import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import AgentStatusPanel from '../../components/navigation/AgentStatusPanel';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { analyticsService } from '../../services/analyticsService';
import LearningPathsSection from './components/LearningPathsSection';
import ResourceLibrary from './components/ResourceLibrary';
import PerformanceRecommendations from './components/PerformanceRecommendations';
import ProgressTracker from './components/ProgressTracker';

const AgentTrainingHub = () => {
  const navigate = useNavigate();
  const [agentPanelOpen, setAgentPanelOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [agentMetrics, setAgentMetrics] = useState([]);
  const [selectedRole, setSelectedRole] = useState('all');
  const [activeTab, setActiveTab] = useState('learning-paths');

  useEffect(() => {
    loadAgentData();
  }, []);

  const loadAgentData = async () => {
    try {
      setLoading(true);
      setError('');
      const metrics = await analyticsService?.getAgentAnalytics();
      setAgentMetrics(metrics);
    } catch (err) {
      console.error('Error loading agent data:', err);
      setError(err?.message || 'Failed to load agent data');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'learning-paths', label: 'Learning Paths', icon: 'BookOpen' },
    { id: 'resources', label: 'Resource Library', icon: 'Library' },
    { id: 'recommendations', label: 'Recommendations', icon: 'Target' },
    { id: 'progress', label: 'Progress Tracking', icon: 'TrendingUp' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header onMenuClick={() => setAgentPanelOpen(!agentPanelOpen)} />
      <AgentStatusPanel 
        isOpen={agentPanelOpen} 
        onClose={() => setAgentPanelOpen(false)} 
        onToggle={() => setAgentPanelOpen(!agentPanelOpen)}
      />

      <main className="pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  Agent Training Hub
                </h1>
                <p className="text-muted-foreground">
                  Centralized skill development resources and performance-driven learning paths
                </p>
              </div>
              <Button
                variant="outline"
                iconName="Download"
                onClick={() => {
                  const csvContent = 'Training Progress Report\n';
                  const blob = new Blob([csvContent], { type: 'text/csv' });
                  const url = window.URL?.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `training-report-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
                  a?.click();
                  window.URL?.revokeObjectURL(url);
                }}
              >
                Export Report
              </Button>
            </div>

            {/* Role Filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Filter by role:</span>
              <div className="flex gap-2">
                {['all', 'coordinator', 'validator', 'analyst', 'auditor', 'specialist']?.map(role => (
                  <button
                    key={role}
                    onClick={() => setSelectedRole(role)}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      selectedRole === role
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    {role === 'all' ? 'All Roles' : role?.charAt(0)?.toUpperCase() + role?.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-border mb-6">
            <div className="flex gap-1">
              {tabs?.map(tab => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary font-medium' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 text-destructive">
                <Icon name="AlertCircle" size={20} />
                <span className="font-medium">{error}</span>
              </div>
            </div>
          )}

          {/* Tab Content */}
          {!loading && !error && (
            <div className="space-y-6">
              {activeTab === 'learning-paths' && (
                <LearningPathsSection selectedRole={selectedRole} />
              )}
              {activeTab === 'resources' && (
                <ResourceLibrary selectedRole={selectedRole} />
              )}
              {activeTab === 'recommendations' && (
                <PerformanceRecommendations 
                  agentMetrics={agentMetrics} 
                  selectedRole={selectedRole}
                />
              )}
              {activeTab === 'progress' && (
                <ProgressTracker 
                  agentMetrics={agentMetrics}
                  selectedRole={selectedRole}
                />
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AgentTrainingHub;